import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type {
  BrandContentResponseModel,
  PreferenceDto,
  SectionContentResponseModel,
  ShopBrandsListApiElementContentModel,
} from '@/services/platform/api/model';
import { cn, ModuleProperties, useLocaleStore } from '@/lib/core';
import { useUIStore } from '@/lib/core/stores/useUIStore';
import { useApp } from '@/lib/core/providers/appProvider';
import ModuleWrapper from '@/components/shared/module/wrapper';
import { getSectionById, getShopBrands } from '@/services/platform/data/shop/catalog';
import { deletePreference, getUserPreferences, setPreference } from '@/services/platform/data/preferences';
import { ShopBrands } from '@/components/shared/shop/brands';

export const ShopBrandsListApi = (props: ModuleProperties) => {
  const content = props.content as ShopBrandsListApiElementContentModel | undefined;
  const locale = useLocaleStore(state => state.locale);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, appSettings } = useApp();
  const setScreenHeadOverride = useUIStore(state => state.setScreenHeadOverride);

  const segments = location.pathname.split('/').filter(Boolean);
  const brands_segment = segments?.pop() ?? '';
  const section_id = brands_segment.includes('_') ? brands_segment.split('_').slice(1).join('_') : '';
  const section_segment = segments?.[segments.length - 3];

  const [section, setSection] = useState<SectionContentResponseModel | undefined>(undefined);
  const [brands, setBrands] = useState<BrandContentResponseModel[]>([]);
  const [userPreferences, setUserPreferences] = useState<PreferenceDto[]>([]);
  const [prefRefetch, setPrefRefetch] = useState(0);

  useEffect(() => {
    getSectionById(section_id, locale)
      .then(result => {
        setSection(result);
        setScreenHeadOverride({
          title: props?.context?.page?.name,
          titleSuffix: result?.name ?? section_segment,
        });
      })
      .catch(() => setSection(undefined));
    return () => setScreenHeadOverride(null);
  }, [section_id, locale]);

  useEffect(() => {
    if (!appSettings) return;
    getShopBrands(appSettings, locale, section?.properties?.slug)
      .then(result => setBrands(result ?? []))
      .catch(() => setBrands([]));
  }, [appSettings, locale, section]);

  useEffect(() => {
    getUserPreferences(locale)
      .then(prefs => setUserPreferences(prefs ?? []))
      .catch(() => setUserPreferences([]));
  }, [locale, prefRefetch]);

  async function handleBookmarkToggle(brand: BrandContentResponseModel, isSelected: boolean) {
    if (isSelected) {
      const pref = userPreferences.find(p => p.subcategoryNodeKey === brand.id);
      if (pref?.publicGuid) await deletePreference(pref.publicGuid, locale);
    } else {
      await setPreference(
        {
          categoryNodeKey: section?.id ?? section_id,
          category: section?.name ?? undefined,
          subcategoryNodeKey: brand.id,
          subcategory: brand.name ?? undefined,
          value: brand.properties?.externalId ?? brand.name ?? '',
        },
        locale
      );
    }
    setPrefRefetch(v => v + 1);
  }

  function handleBrandClick(brand: BrandContentResponseModel) {
    navigate(`${location.pathname}/brand_${brand.id}`);
  }

  function handleBack() {
    setScreenHeadOverride({
      title: props?.context?.page?.name,
      titleSuffix: section?.name ?? section_segment,
      onBack: () => navigate(-1),
    });
  }

  if (!content) return null;

  return (
    <ModuleWrapper
      component={props}
      classNames={{
        base: cn(['module__shop-brands-list']),
        container: cn(['flex-1']),
        wrapper: cn(['py-0', 'flex-1']),
        grid: cn(['flex flex-col flex-1 gap-0']),
      }}
      isFullWidth={true}
      {...props}
    >
      <ShopBrands
        brands={brands}
        userPreferences={userPreferences}
        hasActiveMembership={user?.hasActiveMembership ?? false}
        onBookmarkToggle={handleBookmarkToggle}
        onBrandClick={handleBrandClick}
        onBack={handleBack}
      />
    </ModuleWrapper>
  );
};
